/* eslint-disable react-perf/jsx-no-new-function-as-prop, react-perf/jsx-no-new-object-as-prop, react-perf/jsx-no-new-array-as-prop */
import React from 'react'

import { Box, CircularProgress, TablePagination } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Txt, { TxtProps } from './Txt'
import IconButton from './IconButton'
import { IconName } from '../utils/iconNameDict'

export type SimpleTableObject = {
	id: string
	__type: string
	rows?: SimpleTableObject[]
	__dict?: Record<string, string>
	emptyRowsFallBackContextType?: string
	[key: string]: unknown
}
type CellObject = { id: string; text?: string; jsx?: JSX.Element; isEditable?: boolean }

const StatfulTableCell = ({
	id,
	text,
	jsx,
	numberOfLines,
	textVariant,
	isBold = false,
}: {
	id: string
	text?: string
	jsx?: JSX.Element
	numberOfLines?: number
	textVariant?: TxtProps['variant']
	isBold?: boolean
}) => {
	const baseSx =
		id === 'expand'
			? {
					width: '2rem',
					maxWidth: '2rem',
					minWidth: '2rem',
					textAlign: 'center',
					padding: 0,
			  }
			: id === 'actions'
			? { width: '1%', whiteSpace: 'nowrap' }
			: undefined

	return (
		<TableCell key={id} align={id === 'actions' ? 'right' : 'left'} sx={baseSx}>
			{text ? (
				<Txt
					numberOfLines={numberOfLines}
					variant={textVariant}
					noUnderLine
					fontWeight={isBold ? 'bold' : undefined}
				>
					{text}
				</Txt>
			) : jsx ? (
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.35rem' }}>{jsx}</div>
			) : null}
		</TableCell>
	)
}

const Row = ({
	cells,
	textVariant,
	numberOfLines,
	isBold = false,
	highlight = false,
}: {
	cells: CellObject[]
	textVariant?: TxtProps['variant']
	numberOfLines?: number
	onChange?: (id: string, value: string) => Promise<void>
	isBold?: boolean
	highlight?: boolean
}) => {
	const [hover, setHover] = React.useState(false)
	const handleEnter = React.useCallback(() => setHover(true), [])
	const handleLeave = React.useCallback(() => setHover(false), [])
	const renderActionsCell = React.useCallback(
		(jsx: JSX.Element) => (
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '0.35rem',
					alignItems: 'center',
					width: '100%',
				}}
			>
				{React.Children.toArray(jsx.props?.children || []).map((child, index) => (
					<span
						key={index}
						style={
							index === 0
								? { visibility: hover ? 'visible' : 'hidden', opacity: hover ? 1 : 0.5 }
								: { visibility: hover ? 'visible' : 'hidden', opacity: hover ? 1 : 0.5 }
						}
					>
						{child}
					</span>
				))}
			</div>
		),
		[hover]
	)

	return (
		<TableRow
			onMouseEnter={handleEnter}
			onMouseLeave={handleLeave}
			sx={{
				backgroundColor: highlight ? 'rgba(140, 219, 144, 0.25)' : 'inherit',
				transition: 'background-color 0.5s ease',
			}}
		>
			{cells.map(({ id, text, jsx }) => (
				<StatfulTableCell
					key={id}
					id={id}
					text={text}
					jsx={id === 'actions' && jsx && React.isValidElement(jsx) ? renderActionsCell(jsx) : jsx}
					numberOfLines={numberOfLines}
					textVariant={textVariant}
					isBold={isBold}
				/>
			))}
		</TableRow>
	)
}

const SimpleTable3 = ({
	objects,
	renderActions,
	renderHeadActions,
	activeKeys,
	inactiveKeys,
	numberOfLines,
	dict,
	hideHead = false,
	boldRows = true,
	expandSignal = 0,
	collapseSignal = 0,
	nestingLevel = 0,
	onEmptyAction,
	emptyActionIconName = 'Add',
	disableExpandIcons = false,
	parentId,
	emptyRowsFallBackContextType,
	reserveExpandSpace = false,
	focusId,
	focusSignal,
	highlightId,
	highlightSignal,
	disablePagination = true,
}: {
	objects: SimpleTableObject[]
	renderActions?: (props) => JSX.Element
	renderHeadActions?: (args: { parentId?: string; __type: string }) => JSX.Element
	activeKeys?: string[]
	inactiveKeys?: string[]
	numberOfLines?: number
	dict?: Record<string, string>
	hideHead?: boolean
	boldRows?: boolean
	expandSignal?: number
	collapseSignal?: number
	nestingLevel?: number
	onEmptyAction?: () => void
	emptyActionIconName?: IconName
	disableExpandIcons?: boolean
	parentId?: string
	emptyRowsFallBackContextType?: string
	reserveExpandSpace?: boolean
	focusId?: string
	focusSignal?: number
	highlightId?: string
	highlightSignal?: number
	disablePagination?: boolean
}) => {
	const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
	const expandSignalRef = React.useRef(expandSignal)
	const collapseSignalRef = React.useRef(collapseSignal)
	const [emptyHover, setEmptyHover] = React.useState(false)
	const [headHover, setHeadHover] = React.useState(false)
	const [page, setPage] = React.useState(0)
	const [rowsPerPage, setRowsPerPage] = React.useState(5)
	const [activeHighlightId, setActiveHighlightId] = React.useState<string | null>(null)
	const [highlightOn, setHighlightOn] = React.useState(false)
	const paginationDisabled = disablePagination || nestingLevel > 0

	React.useEffect(() => {
		if (expandSignalRef.current !== expandSignal && !disableExpandIcons) {
			expandSignalRef.current = expandSignal
			const next = new Set<string>()
			objects?.forEach((o) => {
				if (Array.isArray(o.rows)) next.add(o.id)
			})
			setExpanded(next)
		}
	}, [disableExpandIcons, expandSignal, objects])

	React.useEffect(() => {
		if (collapseSignalRef.current !== collapseSignal && !disableExpandIcons) {
			collapseSignalRef.current = collapseSignal
			setExpanded(new Set())
		}
	}, [collapseSignal, disableExpandIcons])

	const mergedInactiveKeys = React.useMemo(
		() =>
			!inactiveKeys
				? ['__typename', '__type', 'rows', '__dict']
				: [...inactiveKeys, '__typename', '__type', 'rows', '__dict'],
		[inactiveKeys]
	)

	const isLoading = objects === undefined || objects === null
	const effectiveObjects = isLoading ? [] : objects

	const baseKeys =
		effectiveObjects && effectiveObjects.length
			? Object.keys(effectiveObjects[0])
			: activeKeys
			? activeKeys
			: dict
			? Object.keys(dict)
			: []

	const filteredKeys = baseKeys
		.filter((key) => (!activeKeys ? true : activeKeys.some((activeKey) => activeKey === key)))
		.filter((key) =>
			!mergedInactiveKeys ? true : !mergedInactiveKeys.some((inactiveKey) => inactiveKey === key)
		)

	// Show expand icons for levels up to option groups, hide for option rows
	const showExpand = reserveExpandSpace || (!disableExpandIcons && nestingLevel < 3)

	const itemType =
		effectiveObjects && effectiveObjects.length
			? effectiveObjects[0].__type
			: emptyRowsFallBackContextType

	const headData: CellObject[] = [
		...(showExpand ? [{ id: 'expand', text: '' }] : []),
		...filteredKeys.map((key) => ({ id: key, text: dict ? dict[key] || key : key })),
	]
	if (renderActions || renderHeadActions)
		headData.push({
			id: 'actions',
			text: renderHeadActions ? undefined : ' ',
			jsx: renderHeadActions ? renderHeadActions({ parentId, __type: itemType }) : undefined,
		})

	const totalCount = effectiveObjects.length || 0
	React.useEffect(() => {
		if (paginationDisabled) return
		if (page * rowsPerPage >= totalCount) {
			setPage(0)
		}
	}, [page, rowsPerPage, totalCount, paginationDisabled])
	const pagedObjects = React.useMemo(() => {
		if (paginationDisabled) return effectiveObjects
		return effectiveObjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
	}, [effectiveObjects, page, paginationDisabled, rowsPerPage])

	// focus newly added row
	const lastFocusSignalRef = React.useRef<number | undefined>(undefined)
	React.useEffect(() => {
		if (!focusId) return
		if (focusSignal !== undefined && lastFocusSignalRef.current === focusSignal) return
		lastFocusSignalRef.current = focusSignal
		const idx = effectiveObjects.findIndex((o) => String(o.id) === String(focusId))
		if (idx === -1) return
		const targetPage = Math.floor(idx / rowsPerPage)
		if (targetPage !== page) setPage(targetPage)
	}, [focusId, focusSignal, effectiveObjects, rowsPerPage, page])

	// highlight newly added or moved row with fade-out
	React.useEffect(() => {
		if (!highlightId) return
		setActiveHighlightId(highlightId)
		setHighlightOn(true)
		const timer = setTimeout(() => setHighlightOn(false), 1000)
		return () => clearTimeout(timer)
	}, [highlightId, highlightSignal])

	if (isLoading) return <CircularProgress />
	if (effectiveObjects.length === 0 && !renderHeadActions)
		return (
			<Box
				mt="0.5rem"
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				gap="0.5rem"
				onMouseEnter={() => setEmptyHover(true)}
				onMouseLeave={() => setEmptyHover(false)}
				onClick={onEmptyAction}
				sx={{
					cursor: onEmptyAction ? 'pointer' : 'default',
					borderRadius: '0.35rem',
					padding: '0.5rem 0.75rem',
					transition: 'background-color 0.2s ease',
					backgroundColor: onEmptyAction && emptyHover ? 'rgba(0,0,0,0.04)' : 'transparent',
				}}
			>
				<Txt variant="body2">Keine Einträge vorhanden</Txt>
				{onEmptyAction ? (
					<IconButton
						iconName={emptyActionIconName}
						sx={{
							opacity: emptyHover ? 1 : 0,
							visibility: emptyHover ? 'visible' : 'hidden',
							transition: 'opacity 0.2s ease, visibility 0.2s ease',
						}}
					/>
				) : null}
			</Box>
		)

	return (
		<TableContainer
			sx={{
				width: '100%',
				paddingLeft: nestingLevel === 0 ? '0rem' : `3rem`,
			}}
		>
			<Table
				sx={{
					'width': '100%',
					'& td': { textAlign: 'left' },
					'& th': { textAlign: 'left' },
				}}
			>
				{hideHead ? null : (
					<TableHead>
						<TableRow
							onMouseEnter={() => setHeadHover(true)}
							onMouseLeave={() => setHeadHover(false)}
							sx={{ height: '2rem' }}
						>
							{headData.map((cell) => (
								<TableCell
									key={cell.id}
									sx={{
										color: 'grey.500',
										py: 0,

										...(cell.id === 'expand'
											? {
													width: '2rem',
													maxWidth: '2rem',
													minWidth: '2rem',
													textAlign: 'center',
													padding: 0,
											  }
											: cell.id === 'actions'
											? { width: '1%', whiteSpace: 'nowrap' }
											: undefined),
									}}
								>
									{cell.text ? (
										<Txt variant="body2" color="grey.500" noUnderLine>
											{cell.text}
										</Txt>
									) : cell.id === 'actions' && cell.jsx ? (
										<Box
											display="flex"
											justifyContent="flex-end"
											sx={{
												visibility: headHover ? 'visible' : 'hidden',
												opacity: headHover ? 1 : 0,
												transition: 'opacity 0.2s ease, visibility 0.2s ease',
												width: '100%',
											}}
										>
											{cell.jsx}
										</Box>
									) : (
										cell.jsx
									)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
				)}

				<TableBody>
					{pagedObjects.map((object, idx) => {
						const childActiveKeys = object.__dict ? Object.keys(object.__dict) : activeKeys
						const childDict = object.__dict || dict
						const isExpanded = showExpand ? expanded.has(object.id) : true
						const isHighlighted =
							highlightOn && activeHighlightId && String(object.id) === String(activeHighlightId)
						const childParentId = object.id
						const rowCells: CellObject[] = [
							...(showExpand
								? [
										{
											id: 'expand',
											jsx:
												disableExpandIcons && reserveExpandSpace ? (
													<Box sx={{ width: '2rem', height: '2rem' }} />
												) : (
													<IconButton
														iconName={isExpanded ? 'KeyboardArrowDown' : 'KeyboardArrowRight'}
														onClick={() => {
															setExpanded((prev) => {
																const next = new Set(prev)
																next.has(object.id) ? next.delete(object.id) : next.add(object.id)
																return next
															})
														}}
														size="small"
														sx={{ width: '2rem', height: '2rem' }}
													/>
												),
										},
								  ]
								: []),
							...Object.entries(object)
								.filter(([key]) => filteredKeys.includes(key))
								.map(([_, value], index) => ({
									id: `${object.id}:${index}`,
									text: String(value),
								})),
							renderActions ? { id: 'actions', jsx: renderActions?.(object.id) } : undefined,
						].filter(Boolean) as CellObject[]

						return (
							<React.Fragment key={object.id || idx}>
								<Row
									cells={rowCells}
									numberOfLines={numberOfLines}
									isBold={false}
									highlight={isHighlighted}
								/>
								{Array.isArray(object.rows) && isExpanded ? (
									<TableRow>
										<TableCell colSpan={headData.length || 1} sx={{ border: 'none', p: 0 }}>
											<SimpleTable3
												objects={object.rows}
												dict={childDict}
												renderActions={renderActions}
												renderHeadActions={renderHeadActions}
												activeKeys={childActiveKeys}
												inactiveKeys={inactiveKeys}
												numberOfLines={numberOfLines}
												boldRows={false}
												nestingLevel={nestingLevel + 1}
												disableExpandIcons={disableExpandIcons}
												parentId={childParentId ? String(childParentId) : undefined}
												emptyRowsFallBackContextType={object.emptyRowsFallBackContextType}
												disablePagination
												highlightId={highlightId}
												highlightSignal={highlightSignal}
											/>
										</TableCell>
									</TableRow>
								) : null}
							</React.Fragment>
						)
					})}
				</TableBody>
			</Table>
			{paginationDisabled ? null : (
				<TablePagination
					component="div"
					count={effectiveObjects.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10))
						setPage(0)
					}}
					rowsPerPageOptions={[5, 10, 20]}
					labelRowsPerPage="Zeilen pro Seite"
				/>
			)}
		</TableContainer>
	)
}
export default React.memo(SimpleTable3)
